import React from 'react';
import styled from 'styled-components';
import { MapPin, Phone, Mail } from 'lucide-react';
import { DealerLocation } from '../types';

interface LocationSelectorProps {
  dealerName: string;
  locations: DealerLocation[];
  onLocationSelect: (location: DealerLocation) => void;
}

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
  font-family: var(--font-family, 'Arial, sans-serif');
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 12px;
`;

const Subtitle = styled.p`
  font-size: 1.125rem;
  color: #6b7280;
  margin: 0;
`;

const LocationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 24px;
  margin-top: 32px;
`;

const LocationCard = styled.button`
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  &:hover {
    border-color: var(--primary-color, #667eea);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
    transform: translateY(-2px);
  }
  
  &:focus {
    outline: none;
    border-color: var(--primary-color, #667eea);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const LocationName = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 16px 0;
`;

const LocationInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.4;
  
  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    margin-top: 2px;
    color: var(--primary-color, #667eea);
  }
`;

const SelectButton = styled.div`
  margin-top: 20px;
  padding: 12px 24px;
  background: var(--primary-color, #667eea);
  color: white;
  border-radius: 8px;
  font-weight: 600;
  text-align: center;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.2s ease;
  
  ${LocationCard}:hover & {
    opacity: 1;
    transform: translateY(0);
  }
`;

const LocationSelector: React.FC<LocationSelectorProps> = ({
  dealerName,
  locations,
  onLocationSelect
}) => {
  return (
    <Container>
      <Header>
        <Title>Select a Location</Title>
        <Subtitle>Choose your preferred {dealerName} location to schedule your appointment</Subtitle>
      </Header>

      <LocationGrid>
        {locations.map((location) => (
          <LocationCard
            key={location.id}
            onClick={() => onLocationSelect(location)}
          >
            <LocationName>{location.name}</LocationName>
            
            <LocationInfo>
              {(location.address || location.city || location.state) && (
                <InfoItem>
                  <MapPin />
                  <div>
                    {location.address && <div>{location.address}</div>}
                    {(location.city || location.state) && (
                      <div>
                        {location.city}
                        {location.city && location.state && ', '}
                        {location.state}
                        {location.zipCode && ` ${location.zipCode}`}
                      </div>
                    )}
                  </div>
                </InfoItem>
              )}
              
              {location.phone && (
                <InfoItem>
                  <Phone />
                  <div>{location.phone}</div>
                </InfoItem>
              )}
              
              {location.email && (
                <InfoItem>
                  <Mail />
                  <div>{location.email}</div>
                </InfoItem>
              )}
            </LocationInfo>

            <SelectButton>
              Select This Location
            </SelectButton>
          </LocationCard>
        ))}
      </LocationGrid>
    </Container>
  );
};

export default LocationSelector;
